import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import { toast } from 'sonner'
import { DragDropContext } from 'react-beautiful-dnd'
import { Lock, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useConfirmation } from '@/context/ConfirmationContext'
import {
  DraggableFieldArray,
  reorderFieldArray,
} from '@/components/shared/DraggableFieldArray'
import { ProductFileRowEditor } from '@/components/shared/inputComponents/ProductFileRowEditor'
import {
  CLAIM_PRODUCT_FILE,
  SET_PRODUCT_FILE_SUPPRESSED,
} from '../productMutations'

export interface ProductFileRecord {
  id: number
  s3_key: string
  url: string
  edition: string | null
  name: string
  display_order: number
  erp_managed: boolean
  suppressed: boolean
}

interface ProductFilesCardProps {
  form: UseFormReturn<any>
  /** Both partitions, including suppressed — the raw `product_files(includeSuppressed: true)` result. */
  files: ProductFileRecord[]
  /** GraphQL operation names to refetch after Claim / Hide / Show. */
  refetchQueries: string[]
}

/**
 * Files section for a product edit form: an editable list of hand-attached
 * (erp_managed: false) catalogue PDFs, plus a read-only list of ERP-owned
 * ones. The two are structurally separate on purpose -- the hand-attached
 * list is bound to `product_files` on the parent form and round-trips
 * through updateProductFiles (a full replace scoped to erp_managed: false),
 * while the ERP list never enters form state: updateProductFiles can only see
 * the erp_managed: false partition, so an ERP row sent back through it is
 * read as a NEW hand-attached file and fails on the unique (product, key)
 * index the ERP row already holds -- which is what claimProductFile is for.
 *
 * Claim and Hide/Show fire immediately against the server rather than
 * waiting for the form's Save, mirroring how e.g. AdditionalComponentEditForm
 * treats delete: this isn't form state, it's an ownership/visibility change
 * with its own confirmation.
 */
export function ProductFilesCard({ form, files, refetchQueries }: ProductFilesCardProps) {
  const { showConfirmation } = useConfirmation()
  const [erpFiles, setErpFiles] = useState<ProductFileRecord[]>(() =>
    files.filter(f => f.erp_managed).sort((a, b) => a.display_order - b.display_order)
  )

  // Resynced whenever the owning query refetches -- after a full form Save, and
  // after any of the three mutations below.
  useEffect(() => {
    setErpFiles(
      files.filter(f => f.erp_managed).sort((a, b) => a.display_order - b.display_order)
    )
  }, [files])

  const [claimProductFile] = useMutation(CLAIM_PRODUCT_FILE, { refetchQueries })
  const [setProductFileSuppressed] = useMutation(SET_PRODUCT_FILE_SUPPRESSED, {
    refetchQueries,
  })

  // THESE THREE ACTIONS RELOAD THE WHOLE FORM.
  //
  // Each of them refetches GetAdvancedProductDetails / GetSimpleProductDetails.
  // That new `productData` re-runs the parent form's hydration effect, which
  // calls `form.reset(...)` -- so every unsaved edit anywhere in the product
  // form (profiles, reference, the file rows below) is discarded, not just the
  // row that was clicked. The local `setErpFiles` / `form.setValue` patches
  // below do not prevent that; the reset lands on top of them moments later.
  //
  // (An earlier comment here claimed the opposite -- that these actions "never
  // disturb unsaved edits". They always have. Dropping `refetchQueries` does
  // not fix it either: the mutations select the same `ProductFile` fields, so
  // Apollo's normalized cache updates the entity in place and `productData`
  // changes identity anyway, with the same reset as the consequence.)
  //
  // So the honest thing is to ask first, and only when there is something to
  // lose.
  const confirmIfDirty = (
    props: { title: string; description: string; confirmText: string },
    run: () => void
  ) => {
    const dirty = form.formState.isDirty
    if (!dirty) {
      run()
      return
    }
    showConfirmation({
      ...props,
      description: `${props.description} You have unsaved changes to this product — they will be discarded, because this reloads the form from the server.`,
      cancelText: 'Cancel',
      onConfirm: run,
    })
  }

  const handleFilesDragEnd = (result: any) => {
    const { source, destination } = result
    if (!destination || source.droppableId !== destination.droppableId) return
    const current = form.getValues('product_files') ?? []
    form.setValue(
      'product_files',
      reorderFieldArray(current, source.index, destination.index),
      { shouldDirty: true }
    )
  }

  const onClaim = (file: ProductFileRecord) => {
    showConfirmation({
      title: 'Claim this file?',
      description:
        `"${file.name}" will become a hand-attached file. This is one-directional — the nightly ERP import will no longer be able to update, resuppress, or recreate this row, ` +
        `as long as it keeps pointing at the same file (repointing it at a different PDF replaces the row and hands the original back to the import; changing only the ?etag= part is safe).` +
        (form.formState.isDirty
          ? ' You have unsaved changes to this product — they will be discarded, because this reloads the form from the server.'
          : ''),
      confirmText: 'Claim',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const { data } = await claimProductFile({ variables: { id: file.id } })
          const claimed = data?.claimProductFile
          if (!claimed) return
          setErpFiles(prev => prev.filter(f => f.id !== file.id))
          const current = form.getValues('product_files') ?? []
          form.setValue(
            'product_files',
            [
              ...current,
              {
                _key: crypto.randomUUID(),
                id: claimed.id,
                name: claimed.name,
                edition: claimed.edition ?? '',
                url: claimed.url,
                suppressed: claimed.suppressed,
              },
            ],
            { shouldDirty: true }
          )
          toast.success('File claimed — it is now hand-attached and editable below.')
        } catch {
          toast.error('Failed to claim file')
        }
      },
    })
  }

  const onToggleErpSuppressed = (file: ProductFileRecord) => {
    confirmIfDirty(
      {
        title: file.suppressed ? 'Show this file?' : 'Hide this file?',
        description: file.suppressed
          ? `"${file.name}" will be shown on the storefront again.`
          : `"${file.name}" will be hidden from the storefront. The row is kept, so the nightly ERP import cannot recreate it.`,
        confirmText: file.suppressed ? 'Show' : 'Hide',
      },
      async () => {
        try {
          const { data } = await setProductFileSuppressed({
            variables: { id: file.id, suppressed: !file.suppressed },
          })
          const updated = data?.setProductFileSuppressed
          if (!updated) return
          setErpFiles(prev => prev.map(f => (f.id === file.id ? { ...f, ...updated } : f)))
          toast.success(updated.suppressed ? 'File hidden' : 'File restored')
        } catch {
          toast.error('Failed to update file')
        }
      }
    )
  }

  // Covers the one edge case a hand-attached row can be suppressed: an ERP
  // row hidden via Hide, then Claimed -- claimProductFile doesn't touch
  // `suppressed`, so the claimed row can carry it over. There is no "hide"
  // counterpart here on purpose: a hand-attached row an admin no longer wants
  // shown can simply be removed from the list below (no ERP re-sync will
  // resurrect it), so the only action needed post-claim is undoing an
  // inherited suppression.
  const onUnsuppressHandAttached = (index: number, id: number) => {
    confirmIfDirty(
      {
        title: 'Show this file?',
        description: 'It will be shown on the storefront again.',
        confirmText: 'Show',
      },
      async () => {
        try {
          const { data } = await setProductFileSuppressed({
            variables: { id, suppressed: false },
          })
          if (!data?.setProductFileSuppressed) return
          form.setValue(`product_files.${index}.suppressed`, false, { shouldDirty: false })
          toast.success('File restored')
        } catch {
          toast.error('Failed to update file')
        }
      }
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catalogue files — hand-attached</CardTitle>
        </CardHeader>
        <CardContent>
          <DragDropContext onDragEnd={handleFilesDragEnd}>
            <DraggableFieldArray
              form={form}
              name="product_files"
              droppableId="product-files"
              onAdd={() => ({
                _key: crypto.randomUUID(),
                name: '',
                edition: '',
                url: '',
                suppressed: false,
              })}
              itemTitle={(file, index) => {
                const label = file.name || `File ${index + 1}`
                return file.suppressed ? `${label} — hidden` : label
              }}
              renderItem={index => {
                const file = form.watch(`product_files.${index}`)
                // The url this row has on the server, so the editor can warn
                // that changing it re-keys the row rather than renaming it.
                // Looked up by id from the query result rather than kept in
                // form state, because form state is what the admin is editing.
                const storedUrl = file?.id
                  ? files.find(f => f.id === file.id)?.url
                  : undefined
                return (
                  <div className="space-y-3">
                    <ProductFileRowEditor
                      form={form}
                      basePath={`product_files.${index}`}
                      storedUrl={storedUrl}
                    />
                    {file?.id && file?.suppressed && (
                      <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-2 text-sm">
                        <span className="text-amber-800">
                          Hidden from the storefront (suppressed before being claimed).
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onUnsuppressHandAttached(index, file.id)}
                        >
                          Show
                        </Button>
                      </div>
                    )}
                  </div>
                )
              }}
              addLabel="Add file"
              emptyText="No hand-attached files yet."
            />
          </DragDropContext>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Catalogue files — ERP import</CardTitle>
            <Badge variant="secondary">read-only</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {erpFiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No files attached by the nightly ERP import.
            </p>
          ) : (
            <div className="space-y-2">
              {erpFiles.map(file => (
                <div
                  key={file.id}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg border p-3',
                    file.suppressed && 'opacity-50 bg-muted/40'
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium">{file.name}</span>
                        <Badge variant="secondary" className="shrink-0">
                          ERP
                        </Badge>
                        {file.suppressed && (
                          <Badge variant="outline" className="shrink-0">
                            Hidden
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {file.edition && <span>{file.edition}</span>}
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Open PDF
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => onClaim(file)}>
                      Claim
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onToggleErpSuppressed(file)}
                    >
                      {file.suppressed ? 'Show' : 'Hide'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
